import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { ProjectsService } from '../../services/projects.service';
import { ToastService } from '../../services/general/toast.service';
import { AlertService } from '../../services/general/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-invite-project-members',
  templateUrl: './invite-project-members.component.html',
  styleUrls: ['./invite-project-members.component.scss'],
  standalone: false
})
export class InviteProjectMembersComponent implements OnInit, OnChanges {

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  @Input() projectId: any;
  @Input() canInvite: boolean = false;

  loading = false;
  members: any[] = [];
  dataSource = new MatTableDataSource<any>([]);
  addingMember = false;
  searchTerm = '';

  columnsToDisplay: string[] = ['name', 'email', 'invited_at', 'can_invite_others'];

  newMember = {
    name: '',
    last_name: '',
    email: '',
    can_invite_others: false
  };

  constructor(
    private projectsService: ProjectsService,
    private toast: ToastService,
    private alertService: AlertService,
    private translateService: TranslateService,
    private userService: UserService
  ) { }

  /** Un utente non può autoeliminarsi né autosollecitarsi dai membri del progetto. */
  isSelf(member: any): boolean {
    const currentEmail = this.userService.userInfo?.email;
    return !!currentEmail && !!member?.email && currentEmail.toLowerCase() === member.email.toLowerCase();
  }

  ngOnInit(): void {
    this.dataSource.filterPredicate = (member: any, filter: string) => {
      const haystack = `${member.name ?? ''} ${member.last_name ?? ''} ${member.email ?? ''}`.toLowerCase();
      return haystack.includes(filter);
    };
    this.updateColumns();
    this.loadMembers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['canInvite'] && !changes['canInvite'].firstChange) {
      this.updateColumns();
    }
  }

  private updateColumns(): void {
    this.columnsToDisplay = this.canInvite
      ? ['name', 'email', 'invited_at', 'can_invite_others', 'actions']
      : ['name', 'email', 'invited_at', 'can_invite_others'];
  }

  loadMembers(): void {
    if (!this.projectId) return;
    this.loading = true;
    this.projectsService.getProjectMembers(this.projectId, (result) => {
      this.members = result || [];
      this.dataSource.data = this.members;
      setTimeout(() => {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
      });
      this.loading = false;
    }, () => {
      this.loading = false;
    });
  }

  onSearchChange(value: string): void {
    this.searchTerm = value;
    this.dataSource.filter = (value || '').trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  toggleAddMember(): void {
    this.addingMember = !this.addingMember;
    if (!this.addingMember) {
      this.resetNewMemberForm();
    }
  }

  resetNewMemberForm(): void {
    this.newMember = { name: '', last_name: '', email: '', can_invite_others: false };
  }

  addMember(form): void {
    if (!form.form.valid) return;

    this.loading = true;
    this.projectsService.addProjectMember(this.projectId, this.newMember, () => {
      this.loading = false;
      this.addingMember = false;
      this.resetNewMemberForm();
      this.loadMembers();
      this.toast.success(
        this.translateService.instant('generic.success'),
        this.translateService.instant('components.invite-project-members.member-invited-successfully')
      );
    }, () => {
      this.loading = false;
    });
  }

  resendInvitation(member: any): void {
    if (this.isSelf(member)) return;
    this.loading = true;
    this.projectsService.resendProjectMemberInvitations(this.projectId, [member.id], () => {
      this.loading = false;
      this.toast.success(
        this.translateService.instant('generic.success'),
        this.translateService.instant('components.invite-project-members.invitation-resent-successfully')
      );
    }, () => {
      this.loading = false;
    });
  }

  removeMember(member: any): void {
    if (this.isSelf(member)) return;
    this.alertService.showQuestion(
      this.translateService.instant('components.decision-table.show-decision-table.proposal.delete-alert-title'),
      this.translateService.instant('components.invite-project-members.confirm-remove-member'),
      () => {
        this.loading = true;
        this.projectsService.deleteProjectMembers(this.projectId, [member.id], () => {
          this.loading = false;
          this.loadMembers();
          this.toast.success(
            this.translateService.instant('generic.success'),
            this.translateService.instant('components.invite-project-members.member-removed-successfully')
          );
        }, () => {
          this.loading = false;
        });
      }
    );
  }
}
